import { useQuery } from '@apollo/client'
import * as Sentry from '@sentry/react'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Channel, ConnectionOpen, Event, StreamChat, UserResponse } from 'stream-chat'

import { chatClientService } from '../../_services'
import { MembershipStatus } from '../../_shared/types/membership-types'
import { StringUtils } from '../../_utils'
import { createGenericContext } from '../../_utils/create-generic-context'
import { tokenService } from '../../auth/data'
import { useAuth } from '../../auth/data/use-auth'
import { GET_WORKSPACE_MEMBERS, IWorkspaceMember } from '../../settings/members/data/queries'
import { IChatUser } from '../data/types'

/**
 * Constants used throughout the chat context
 */
const CHAT_CONSTANTS = {
  HEALTH_CHECK_INTERVAL: 120000, // 2 minutes
  MAX_BACKOFF_TIME: 30000, // 30 seconds
  MAX_RECONNECT_ATTEMPTS: 2
} as const

/**
 * Chat state interface for managing related state together
 */
interface IChatState {
  connectionStatus: 'connected' | 'connecting' | 'disconnected' | 'error'
  unreadCount: number
  userConnection: ConnectionOpen | null
  workspaceUsers: (UserResponse & IChatUser)[]
}

/**
 * Chat context interface defining all available methods and properties
 */
interface IChatContext extends IChatState {
  chatClient: StreamChat
  disconnectUser: () => void
  searchUser: (inputValue: string) => Promise<IChatUser[] | []>
}

// Create context with type safety
const [useChat, ChatContextProvider] = createGenericContext<IChatContext>(
  'useChat must be used within the ChatContextProvider'
)

interface ChatProviderProps {
  children: React.ReactNode
}

/**
 * ChatProvider component that manages the Stream Chat connection lifecycle
 */
const ChatProvider: React.FC<ChatProviderProps> = ({ children }) => {
  // Combined state management
  const [chatState, setChatState] = useState<IChatState>({
    connectionStatus: 'disconnected',
    unreadCount: 0,
    userConnection: null,
    workspaceUsers: []
  })

  const { userDetails } = useAuth()
  const userChatToken = tokenService.getToken('User-Chat-Token')
  const navigate = useNavigate()
  const reconnectAttempts = useRef(0)
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const connectionStatusRef = useRef<IChatState['connectionStatus']>('disconnected')

  /**
   * TODO: WE should figure out a top layer place to fetch the selected workspace members. For V2 we need to do better design than this, To handle the getstream chat rate limit issue, i am doing this here for now.
   */
  const { data: { workspaceMembers } = {} } = useQuery<{
    workspaceMembers: IWorkspaceMember[]
  }>(GET_WORKSPACE_MEMBERS, {
    fetchPolicy: 'cache-first',
    variables: {
      input: {
        membershipStatuses: [MembershipStatus.ACTIVE]
      }
    }
  })

  /**
   * Creates a standardized error handler for different contexts
   */
  const createErrorHandler = useCallback(
    (context: string) => (error: unknown) => {
      setChatState((prev) => ({ ...prev, connectionStatus: 'error' }))
      Sentry.captureException(error, {
        extra: {
          reconnectAttempts: reconnectAttempts.current,
          userDetails: {
            id: userDetails?.id,
            name: userDetails?.name
          }
        },
        tags: {
          component: 'ChatProvider',
          context,
          userId: userDetails?.id || 'unknown'
        }
      })
      console.error(`Chat connection error in ${context}:`, error)
    },
    [userDetails]
  )

  /**
   * Resets reconnection attempts and clears timeouts
   */
  const resetReconnectAttempts = useCallback(() => {
    reconnectAttempts.current = 0
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current)
      reconnectTimeoutRef.current = null
    }
  }, [])

  /**
   * Schedules a reconnection attempt with exponential backoff
   */
  const scheduleReconnect = useCallback(() => {
    if (reconnectAttempts.current < CHAT_CONSTANTS.MAX_RECONNECT_ATTEMPTS) {
      reconnectAttempts.current += 1

      const backoffTime = Math.min(Math.pow(2, reconnectAttempts.current) * 1000, CHAT_CONSTANTS.MAX_BACKOFF_TIME)

      Sentry.addBreadcrumb({
        category: 'chat',
        data: { backoffTime },
        level: 'info',
        message: `Scheduling reconnection attempt ${reconnectAttempts.current}/${CHAT_CONSTANTS.MAX_RECONNECT_ATTEMPTS}`
      })

      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current)
      }

      reconnectTimeoutRef.current = setTimeout(() => {
        if (chatState.connectionStatus !== 'connected') {
          connectUser()
        }
      }, backoffTime)
    } else {
      Sentry.captureMessage('Maximum Stream Chat reconnection attempts reached', {
        level: 'error',
        tags: {
          component: 'ChatProvider',
          userId: userDetails?.id || 'unknown'
        }
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatState.connectionStatus, userDetails])

  /**
   * Handles Stream Chat events
   */
  const handleStreamEvents = useCallback(
    (event: Event) => {
      if (event.total_unread_count !== null && event.total_unread_count !== undefined) {
        setChatState((prev) => ({ ...prev, unreadCount: event.total_unread_count || 0 }))
      }

      if (event.type === 'connection.changed') {
        if (!event.online) {
          Sentry.addBreadcrumb({
            category: 'chat',
            level: 'warning',
            message: 'Stream Chat connection went offline'
          })
          scheduleReconnect()
        } else {
          Sentry.addBreadcrumb({
            category: 'chat',
            level: 'info',
            message: 'Stream Chat connection restored'
          })
          resetReconnectAttempts()
        }
      }
    },
    [scheduleReconnect, resetReconnectAttempts]
  )

  /**
   * Connects user to Stream Chat
   */
  const connectUser = useCallback(async (): Promise<void> => {
    try {
      if (!chatState.userConnection && userChatToken && userDetails?.id) {
        setChatState((prev) => ({ ...prev, connectionStatus: 'connecting' }))

        Sentry.addBreadcrumb({
          category: 'chat',
          data: {
            attemptNumber: reconnectAttempts.current + 1,
            userId: userDetails.id
          },
          level: 'info',
          message: 'Attempting to connect user to Stream Chat'
        })

        const connection = await chatClientService.client.connectUser(
          {
            id: userDetails.id,
            image: userDetails.photo || '',
            name: userDetails.name || ''
          },
          userChatToken
        )

        chatClientService.client.on(handleStreamEvents)

        // Monitor health check events
        chatClientService.client.on('health.check', (event: Event) => {
          Sentry.addBreadcrumb({
            category: 'chat',
            data: { connectionId: event.connection_id || 'unknown' },
            level: 'info',
            message: 'Health check completed'
          })
        })

        // Listen for connection errors
        chatClientService.client.on('connection.error', (event: Event & { error?: Error }) => {
          createErrorHandler('connection.error event')(event.error)
          scheduleReconnect()
        })

        if (connection) {
          setChatState((prev) => ({
            ...prev,
            connectionStatus: 'connected',
            unreadCount: connection?.me?.total_unread_count || 0,
            userConnection: connection
          }))

          resetReconnectAttempts()

          Sentry.addBreadcrumb({
            category: 'chat',
            data: {
              connectionId: String(connection?.connection_id || 'unknown'),
              userId: userDetails.id
            },
            level: 'info',
            message: 'Successfully connected to Stream Chat'
          })
        }
      }
    } catch (e) {
      console.error('Error connecting to Stream Chat', e)
      // Handle network change errors specifically
      Sentry.addBreadcrumb({
        category: 'chat',
        data: {
          error: 'ERR_NETWORK_CHANGED',
          userId: userDetails?.id
        },
        level: 'warning',
        message: 'Network changed detected during connection, will retry'
      })

      // For connection errors, let the existing reconnection logic handle it
      // since it already has exponential backoff
      scheduleReconnect()

      createErrorHandler('connectUser method')(e)
      scheduleReconnect()
    }
  }, [
    userChatToken,
    chatState.userConnection,
    userDetails,
    createErrorHandler,
    handleStreamEvents,
    scheduleReconnect,
    resetReconnectAttempts
  ])

  /**
   * Disconnects user from Stream Chat
   */
  const disconnectUser = useCallback((): void => {
    try {
      Sentry.addBreadcrumb({
        category: 'chat',
        data: { userId: userDetails?.id },
        level: 'info',
        message: 'Disconnecting user from Stream Chat'
      })

      chatClientService.client.disconnectUser()
      setChatState((prev) => ({
        ...prev,
        connectionStatus: 'disconnected',
        userConnection: null
      }))
      resetReconnectAttempts()
    } catch (e) {
      createErrorHandler('disconnectUser method')(e)
    }
  }, [userDetails, createErrorHandler, resetReconnectAttempts])

  /**
   * Optimized search user implementation
   */
  const searchUser = useCallback(
    async (inputValue: string): Promise<IChatUser[]> => {
      try {
        const channels = await chatClientService.client.queryChannels({
          $and: [{ members: { $in: [userDetails.id] } }, { type: 'messaging' }]
        })

        const lowercasedInput = inputValue.toLowerCase()
        const isEmailSearch = StringUtils.isEmail(inputValue)
        const isPhoneSearch = StringUtils.isPhone(inputValue)

        return channels.flatMap((channel: Channel) => {
          const connectedUserID = Object.keys(channel.state.members).find((key) => key !== userDetails.id)

          if (!connectedUserID) return []

          const userData = channel.state.members[connectedUserID].user as IChatUser
          if (userData?.deleted) return []

          if (isEmailSearch && userData?.email === inputValue) return [userData]
          if (isPhoneSearch && userData?.phone === inputValue) return [userData]
          if (userData?.name?.toLowerCase().includes(lowercasedInput)) return [userData]

          return []
        })
      } catch (e) {
        console.error('Network changed detected, attempting retry', e)
        // Handle network change errors specifically
        console.error('Network changed detected, attempting retry')
        Sentry.addBreadcrumb({
          category: 'chat',
          data: {
            error: 'ERR_NETWORK_CHANGED',
            userId: userDetails?.id
          },
          level: 'warning',
          message: 'Network changed detected, attempting retry'
        })

        // Wait a moment for network to stabilize, then retry once
        await new Promise((resolve) => setTimeout(resolve, 1000))

        try {
          const channels = await chatClientService.client.queryChannels({
            $and: [{ members: { $in: [userDetails.id] } }, { type: 'messaging' }]
          })

          const lowercasedInput = inputValue.toLowerCase()
          const isEmailSearch = StringUtils.isEmail(inputValue)
          const isPhoneSearch = StringUtils.isPhone(inputValue)

          return channels.flatMap((channel: Channel) => {
            const connectedUserID = Object.keys(channel.state.members).find((key) => key !== userDetails.id)

            if (!connectedUserID) return []

            const userData = channel.state.members[connectedUserID].user as IChatUser
            if (userData?.deleted) return []

            if (isEmailSearch && userData?.email === inputValue) return [userData]
            if (isPhoneSearch && userData?.phone === inputValue) return [userData]
            if (userData?.name?.toLowerCase().includes(lowercasedInput)) return [userData]

            return []
          })
        } catch (retryError) {
          createErrorHandler('searchUser method retry')(retryError)
          return []
        }
      }
    },
    [userDetails, createErrorHandler]
  )

  /**
   * TODO: Quick fix to save the unwanted calls of queryUsers for the workspace members, in v2 we need to do better design
   *
   */
  useEffect(() => {
    if (workspaceMembers?.length && chatState.connectionStatus === 'connected') {
      const userIds = workspaceMembers.map((member) => member.userID)

      chatClientService.client
        .queryUsers({ id: { $in: userIds } })
        .then((response) => {
          setChatState((prev) => ({
            ...prev,
            workspaceUsers: response.users as (UserResponse & IChatUser)[]
          }))
        })
        .catch((error) => {
          console.error('Error fetching workspace users:', error)
          Sentry.captureException(error, {
            extra: {
              userIds,
              workspaceMembersCount: workspaceMembers.length
            },
            tags: {
              component: 'ChatProvider',
              context: 'workspaceUsers',
              userId: userDetails?.id || 'unknown'
            }
          })
        })
    }
  }, [workspaceMembers, chatState.connectionStatus, userDetails?.id])

  // Update ref when state changes
  useEffect(() => {
    connectionStatusRef.current = chatState.connectionStatus
  }, [chatState.connectionStatus])

  // Effect for connection management
  useEffect(() => {
    const cleanup = () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current)
        reconnectTimeoutRef.current = null
      }
    }

    if (userChatToken && userDetails?.id) {
      connectUser()
    }

    return () => {
      cleanup()
      if (chatState.userConnection && !userChatToken && userDetails?.id) {
        disconnectUser()
      }
    }
  }, [userChatToken, userDetails?.id, connectUser, disconnectUser, chatState.userConnection])

  // Effect for chat client listeners
  useEffect(() => {
    chatClientService.addListeners()
    return () => chatClientService.removeListeners()
  }, [])

  // Effect for navigation
  useEffect(() => {
    chatClientService.setNavigateInstance(navigate)
  }, [navigate])

  const contextValue: IChatContext = {
    ...chatState,
    chatClient: chatClientService.client,
    disconnectUser,
    searchUser
  }

  return <ChatContextProvider value={contextValue}>{children}</ChatContextProvider>
}

export { ChatContextProvider, ChatProvider, useChat }
